import {
  Component, ElementRef, Input, OnDestroy,
  ViewChild,
} from '@angular/core';
import {FeatureTypeService} from '@catcore/services';
import {DiagramService} from '@catcore/services/diagram/diagram.service';
import {Feature} from '@catcore/services/feature/feature';
import {GenericLinkedResource} from '@catcore/types/base';
import {GenericResourceApiService} from '@catfeatures/services/generic-resource-api.service';
import {CreatePortConnectionComponent} from '@catmodules/models/feature-port/components/create-port-connection/create-port-connection.component';
import {ModifyPortConnectionComponent} from '@catmodules/models/feature-port/components/modify-port-connection/modify-port-connection.component';
import {ModifyUnitComponent} from '@catmodules/models/units/components/modify-unit/modify-unit.component';
import {ModalService} from '@catmodules/widgets/slide-in-modal/services/modal.service';
import {isNullOrUndefined} from 'util';

declare let SystemBlockDiagram: any;

// d3 import is needed by system-block-diagram.js
// noinspection ES6UnusedImports
import {DisplayTypes} from '@catui/components/panel/types';
import * as d3 from 'd3';
import '../../../../lib/blockdiagram-editor/system-block-diagram.js';

@Component({
  selector: 'cat-feature-diagram',
  templateUrl: './feature-diagram.component.html',
  styleUrls: ['./feature-diagram.component.scss'],
})
export class FeatureDiagramComponent implements OnDestroy {

  featureDiagram = null;
  autoLayoutPorts = true;
  panEnabled = false;
  panelDisplayType = DisplayTypes;
  sbdLoaded = false;
  sbdDirty = false;

  @ViewChild('canvas')
  private canvas: ElementRef;

  private _sbd = null;
  private _feature = null;
  private _modalSubscription = null;

  constructor(private el: ElementRef,
              private featureService: FeatureTypeService,
              private diagramService: DiagramService,
              private genericResourceService: GenericResourceApiService,
              private modalService: ModalService) {
  }

  @Input() set feature(value: Feature) {
    this._feature = value;
    this.onFeatureUpdated();
  }

  get feature(): Feature {
    return this._feature;
  }

  onFeatureUpdated() {
    this.sbdLoaded = false;
    if (this._feature) {
      this.featureService
        .getBlockDiagram(this._feature.id)
        .subscribe((blockDiagram: any) => {
          this.featureDiagram = blockDiagram;

          this._sbd = new SystemBlockDiagram(d3);
          this._sbd.renderMenu = false;

          this._sbd.onSetDirty = () => { this.sbdDirty = true; };
          this._sbd.onSave = () => { this.save(); };
          this._sbd.onUnitDblClick = (unit) => { this.unitDblClick(unit); };
          this._sbd.onPortDblClick = (port) => { this.portDblClick(port); };
          this._sbd.onPortConnectionDblClick = (pc) => { this.portConnectionDblClick(pc); };

          this._sbd.setDiagramData(this.featureDiagram);

          while (this.canvas.nativeElement.firstChild) {
            this.canvas.nativeElement.removeChild(this.canvas.nativeElement.firstChild);
          }
          this._sbd.render(this.canvas.nativeElement);
          this._sbd.resizeSvgToContent();
          this._sbd.enableAutoSetPortLocations(this.autoLayoutPorts);
          this._sbd.setPanEnabled(this.panEnabled);
          this.sbdLoaded = true;
        });
    }
  }

  toggleAutoLayoutPorts() {
    this.autoLayoutPorts = !this.autoLayoutPorts;
    if (this._sbd) {
      this._sbd.enableAutoSetPortLocations(this.autoLayoutPorts);
    }
  }

  togglePanEnabled() {
    this.panEnabled = !this.panEnabled;
    if (this._sbd) {
      this._sbd.setPanEnabled(this.panEnabled);
    }
  }

  zoomOut() {
    if (this._sbd) {
      this._sbd.zoomOut();
    }
  }

  zoomIn() {
    if (this._sbd) {
      this._sbd.zoomIn();
    }
  }

  private openDialog(resource: GenericLinkedResource, componentFactory) {
    let compRef;
    const onFullfill = () => {
      this._modalSubscription.unsubscribe();
    };

    const onError = () => {
      this._modalSubscription.unsubscribe();
    };

    const resourceHandler = this
      .genericResourceService
      .getServiceForResource(resource.href);

    if (this._modalSubscription) {
      this._modalSubscription.unsubscribe();
    }

    this._modalSubscription = resourceHandler
      .get()
      .subscribe((item) => {
        if (compRef) {
          compRef.close();
          compRef = null;
        }

        if (isNullOrUndefined(item)) {
          compRef = null;
        } else {
          compRef = componentFactory(item);
          compRef.result.then(onFullfill, onError);
        }
      });
  }

  private openCreateDialog(portResource: GenericLinkedResource) {
    this.openDialog(portResource, (port) => {
      const compRef = this.modalService.open(CreatePortConnectionComponent);
      compRef.componentInstance.title = `New Port Connection`;
      compRef.componentInstance.source = port;
      compRef.componentInstance.afterCreate.subscribe(() => this.refresh());
      return compRef;
    });
  }

  private openModifyDialog(portConnectionResource: GenericLinkedResource) {
    this.openDialog(portConnectionResource, (portConnection) => {
      const compRef = this.modalService.open(ModifyPortConnectionComponent);
      compRef.componentInstance.title = `Modify Port Connection`;
      compRef.componentInstance.model = portConnection;
      compRef.componentInstance.afterUpdate.subscribe(() => this.refresh());
      compRef.componentInstance.afterDelete.subscribe(() => this.refresh());
      return compRef;
    });
  }

  private openModifyUnitDialog(unitResource: GenericLinkedResource) {
    this.openDialog(unitResource, (unit) => {
      const compRef = this.modalService.open(ModifyUnitComponent);
      compRef.componentInstance.unit = unit;
      compRef.componentInstance.afterUpdate.subscribe((updatedUnit) => this.refresh());
      compRef.componentInstance.afterDelete.subscribe(() => this.refresh());
      return compRef;
    });
  }

  refresh() {
    if (this._modalSubscription) {
      this._modalSubscription.unsubscribe();
    }
    this.onFeatureUpdated();
  }

  sbdSave() {
    if (this._sbd) {
      this._sbd.save();
    }
  }

  save() {
    this.diagramService
      .update(this.featureDiagram)
      .subscribe((updates) => {
        this.sbdDirty = false;
      });
  }

  unitDblClick(unit) {
    this.openModifyUnitDialog(unit.source._links.self);
  }

  portDblClick(port) {
    this.openCreateDialog(port.source._links.self);
  }

  portConnectionDblClick(portConnection) {
    this.openModifyDialog(portConnection.source._links.self);
  }

  ngOnDestroy() {
    if (this._modalSubscription) {
      this._modalSubscription.unsubscribe();
    }
  }
}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_develop@2/app/Resources/Public/src/app/modules/features/components/feature-diagram/feature-diagram.component.ts